/**
 * Resource nodes: the store, the index over it, and the walk that seeds it.
 *
 * ── What is here, and what is deliberately not ──────────────────────────────
 *
 * This file imports `constants`, `types`, `map`, `stores` and `data/nodes` and
 * **nothing else — in particular not `world.ts`.** That is load-bearing rather
 * than tidy, for exactly the reason written at the top of `systems/bindings.ts`:
 * `world.ts` has to import this one (a `ResourceNodeStore` is a field of every
 * `World`), and `world.ts → nodes.ts → world.ts` would close a runtime cycle
 * through the file half the simulation imports. So everything here takes a
 * `TileMap` and a `ResourceNodeStore` rather than a world, and the World-level
 * glue — hauling, the dirty flags, the events — lives in
 * `systems/settlement.ts`. **If you add anything to this file, keep it free of
 * `world.ts`.**
 *
 * ── Why a columnar store, measured ──────────────────────────────────────────
 *
 * The brief said to measure and say. On four seeds of the shipped 384×384
 * generator the terrain pass paints 21,068–37,002 forest tiles, 21,296–36,900
 * stone and 4,093–6,378 iron. At the lattices in `data/nodes.ts` that is on the
 * order of **eleven to fourteen thousand nodes on an ordinary map** — the same
 * order as `MAX_UNITS`, an order of magnitude past `w.companies`, and walked in
 * full by `hashWorld` on every determinism check and by `saveWorld` on every
 * save. An array of eleven thousand little objects would be eleven thousand
 * allocations at world creation, a pointer chase per node in the hash, and JSON
 * in the save where the unit store has base64. So: typed arrays, the same
 * `SlotAllocator` the other three stores use, and `saveStore`/`loadStore` work
 * on it unmodified because it has the same shape.
 *
 * ── The index, and why it is allowed to be stale ────────────────────────────
 *
 * `nearestNodes` is asked "where is the closest tree" by every harvest worker
 * whose basket has just been emptied — thousands of workers, though only once
 * per round trip each. A uniform grid answers it in a few cells, on the model of
 * `JobGrid` in `settlement.ts` and `SpatialHash`.
 *
 * The grid is a **derived cache and is neither hashed nor saved**, and it is
 * allowed to carry nodes that have since been felled, because every reader
 * re-checks `alive` and `amount` before trusting an entry — so a stale entry can
 * only ever cost a few wasted comparisons, never change an answer. That is the
 * rule from COORDINATION.md applied honestly: what may not be cached is anything
 * whose staleness is *observable*. A node that has been **added**, by contrast,
 * would be invisible, so an add forces a rebuild; adds happen at seeding and
 * nowhere else.
 */

import { HARVEST_RADIUS, MAP_H, MAP_TILES, MAP_W, MAX_NODES } from '../constants.ts';
import { alliedWith, Team, Terrain, WallKind } from '../types.ts';
import { SlotAllocator } from '../stores.ts';
import type { TileMap } from '../map.ts';
import { NODE_KINDS, nodeKind, nodeKindForTerrain } from '../../data/nodes.ts';
import { hash2 } from '../../core/math.ts';

/**
 * Its own salt, per the rule the four `Rng` streams follow: a shared hash
 * stream means a new consumer silently retunes every system downstream of it.
 */
const SALT_SEED_SCATTER = 0x5c2b;

/**
 * One discrete thing standing on one tile.
 *
 * `tile` rather than an (x, y) pair, because every question asked of a node is
 * really a question about its tile — is the ground under it still forest, is
 * there a building on it now, what does it revert to — and a single `Int32` is
 * one column instead of two, one hash write instead of two, and cannot get out
 * of step with itself. The centre of the tile is the node's position.
 */
export class ResourceNodeStore {
  readonly slots = new SlotAllocator(MAX_NODES);

  /** Tile index. `x = tile % MAP_W`, `y = (tile / MAP_W) | 0`. */
  readonly tile = new Int32Array(MAX_NODES);
  /** A `NodeKind`. */
  readonly kind = new Uint8Array(MAX_NODES);
  /** How much is left in it, in resource units. */
  readonly amount = new Float32Array(MAX_NODES);
  /** What it held when it was seeded, so a UI can draw a bar. */
  readonly max = new Float32Array(MAX_NODES);
  /** Saved physical width. Old saves retain their original single-tile nodes. */
  readonly footprint = new Uint8Array(MAX_NODES).fill(1);
  /** Derived movement occupancy, shared with TileMap; never saved or hashed. */
  readonly solidTiles = new Uint8Array(MAP_TILES);

  // ── Derived: never hashed, never saved ───────────────────────────────────
  /** Which node stands on a tile, or −1. Maintained incrementally. */
  private readonly byTile = new Int32Array(MAP_TILES).fill(-1);
  /** Only legacy saves can overlap newly enlarged footprints. Preserve every reserve. */
  private readonly overlaps = new Map<number, number[]>();
  private readonly starts = new Int32Array(CELL_COUNT + 1);
  private readonly cursor = new Int32Array(CELL_COUNT);
  private readonly items = new Int32Array(MAX_NODES);
  private readonly cellOf = new Int32Array(MAX_NODES);
  /** Bitfield of kinds present per cell, so a query for ore skips the woods. */
  private readonly cellMask = new Uint8Array(CELL_COUNT);
  private gridDirty = true;
  private deadSinceRebuild = 0;
  /** Live nodes per kind, so a query for a kind nobody has costs nothing. */
  private readonly kindTotals = new Int32Array(8);

  spawn(tile: number, kind: number, amount: number): number {
    const bounds = footprintBounds(tile, kind);
    if (bounds.x0 < 0 || bounds.y0 < 0 || bounds.x1 >= MAP_W || bounds.y1 >= MAP_H) return -1;
    for (let y = bounds.y0; y <= bounds.y1; y++) for (let x = bounds.x0; x <= bounds.x1; x++) {
      if (this.at(y * MAP_W + x) >= 0) return -1;
    }
    const i = this.slots.alloc();
    if (i < 0) return -1;
    this.tile[i] = tile;
    this.kind[i] = kind;
    this.amount[i] = amount;
    this.max[i] = amount;
    this.footprint[i] = nodeKind(kind).footprint;
    this.indexNode(i);
    this.kindTotals[kind & 7]++;
    this.gridDirty = true;
    return i;
  }

  kill(i: number): void {
    if (this.slots.alive[i] === 0) return;
    const bounds = nodeFootprintBounds(this, i);
    for (let y = Math.max(0, bounds.y0); y <= Math.min(MAP_H - 1, bounds.y1); y++) {
      for (let x = Math.max(0, bounds.x0); x <= Math.min(MAP_W - 1, bounds.x1); x++) {
        const tile = y * MAP_W + x, others = this.overlaps.get(tile);
        if (others) {
          const at = others.indexOf(i);
          if (at >= 0) others.splice(at, 1);
          this.byTile[tile] = others[0] ?? -1;
          if (others.length <= 1) this.overlaps.delete(tile);
        } else if (this.byTile[tile] === i) this.byTile[tile] = -1;
        const remaining = this.overlaps.get(tile);
        const node = this.byTile[tile];
        this.solidTiles[tile] = remaining ? Number(remaining.some(n => this.footprint[n] > 1)) : Number(node >= 0 && this.footprint[node] > 1);
      }
    }
    this.kindTotals[this.kind[i] & 7]--;
    this.slots.release(i);
    this.deadSinceRebuild++;
  }

  isAlive(i: number): boolean {
    return this.slots.alive[i] === 1;
  }

  at(tile: number): number {
    if (tile < 0 || tile >= MAP_TILES) return -1;
    const i = this.byTile[tile];
    return i >= 0 && this.slots.alive[i] === 1 ? i : -1;
  }

  get count(): number {
    return this.slots.count;
  }

  countOfKind(kind: number): number {
    return this.kindTotals[kind & 7];
  }

  reset(): void {
    this.slots.reset();
    this.footprint.fill(1);
    this.solidTiles.fill(0);
    this.byTile.fill(-1);
    this.overlaps.clear();
    this.kindTotals.fill(0);
    this.gridDirty = true;
    this.deadSinceRebuild = 0;
  }

  /**
   * Rebuild the tile index and the totals from the columns.
   *
   * `loadStore` writes straight into the typed arrays and knows nothing about
   * either, so a loaded world has to be told. Called once from `loadWorld`.
   */
  reindex(): void {
    this.byTile.fill(-1);
    this.solidTiles.fill(0);
    this.overlaps.clear();
    this.kindTotals.fill(0);
    for (let i = 0; i < this.slots.high; i++) {
      if (this.slots.alive[i] === 0) continue;
      this.indexNode(i);
      this.kindTotals[this.kind[i] & 7]++;
    }
    this.gridDirty = true;
    this.deadSinceRebuild = 0;
  }

  private indexNode(i: number): void {
    const b = nodeFootprintBounds(this, i);
    for (let y = Math.max(0, b.y0); y <= Math.min(MAP_H - 1, b.y1); y++) {
      for (let x = Math.max(0, b.x0); x <= Math.min(MAP_W - 1, b.x1); x++) {
        const tile = y * MAP_W + x, previous = this.byTile[tile];
        if (this.footprint[i] > 1) this.solidTiles[tile] = 1;
        if (previous < 0) this.byTile[tile] = i;
        else {
          const list = this.overlaps.get(tile) ?? [previous];
          list.push(i);
          this.overlaps.set(tile, list);
        }
      }
    }
  }

  private rebuildGrid(): void {
    this.starts.fill(0);
    this.cellMask.fill(0);
    const high = this.slots.high;
    for (let i = 0; i < high; i++) {
      if (this.slots.alive[i] === 0) continue;
      const t = this.tile[i];
      const c = cellIndex(t % MAP_W, (t / MAP_W) | 0);
      this.cellOf[i] = c;
      this.starts[c + 1]++;
      this.cellMask[c] |= 1 << (this.kind[i] & 7);
    }
    for (let c = 0; c < CELL_COUNT; c++) {
      this.starts[c + 1] += this.starts[c];
      this.cursor[c] = this.starts[c];
    }
    for (let i = 0; i < high; i++) {
      if (this.slots.alive[i] === 0) continue;
      this.items[this.cursor[this.cellOf[i]]++] = i;
    }
    this.gridDirty = false;
    this.deadSinceRebuild = 0;
  }

  private ensureGrid(): void {
    if (this.gridDirty || this.deadSinceRebuild > GRID_REBUILD_DEAD) this.rebuildGrid();
  }

  /**
   * The nearest live nodes of `kind` within `radius` tiles of (x, y), nearest
   * first, up to `out.length` of them. Returns how many were found.
   *
   * More than one, because a three-man camp all felling the same tree looks like
   * a queue rather than a gang. The caller picks by worker index — see
   * `claimNode` in `settlement.ts` — which spreads a shift over the nearest few
   * without any of the "who has claimed what" bookkeeping a reservation would
   * need, and without a scrap of state.
   */
  nearestNodes(x: number, y: number, radius: number, kind: number, out: Int32Array): number {
    if (this.kindTotals[kind & 7] <= 0) return 0;
    this.ensureGrid();
    const want = out.length;
    const mask = 1 << (kind & 7);
    const cx0 = clampCell(Math.floor(x / NODE_CELL), CELLS_X);
    const cy0 = clampCell(Math.floor(y / NODE_CELL), CELLS_Y);
    const maxRing = Math.ceil(radius / NODE_CELL);
    const r2 = radius * radius;
    let n = 0;
    let worst = r2;

    for (let ring = 0; ring <= maxRing; ring++) {
      const xLo = cx0 - ring;
      const xHi = cx0 + ring;
      const yLo = cy0 - ring;
      const yHi = cy0 + ring;
      for (let cy = yLo; cy <= yHi; cy++) {
        if (cy < 0 || cy >= CELLS_Y) continue;
        const onYEdge = cy === yLo || cy === yHi;
        for (let cx = xLo; cx <= xHi; cx++) {
          if (cx < 0 || cx >= CELLS_X) continue;
          if (!onYEdge && cx !== xLo && cx !== xHi) continue;
          const c = cy * CELLS_X + cx;
          if ((this.cellMask[c] & mask) === 0) continue;
          const s = this.starts[c];
          const e = this.starts[c + 1];
          for (let k = s; k < e; k++) {
            const i = this.items[k];
            // The stale-entry re-check. See the note at the top of the file.
            if (this.slots.alive[i] === 0) continue;
            if (this.kind[i] !== kind) continue;
            if (this.amount[i] <= 0) continue;
            const t = this.tile[i];
            const dx = (t % MAP_W) + 0.5 - x;
            const dy = ((t / MAP_W) | 0) + 0.5 - y;
            const d2 = dx * dx + dy * dy;
            if (d2 > r2) continue;
            if (n === want && d2 >= worst) continue;
            // Insertion sort into a list of at most four. Nothing cleverer is
            // worth the branches at this size.
            let at = n < want ? n++ : want - 1;
            while (at > 0 && distTo(this, out[at - 1], x, y) > d2) {
              out[at] = out[at - 1];
              at--;
            }
            out[at] = i;
            worst = n === want ? distTo(this, out[want - 1], x, y) : r2;
          }
        }
      }
      // A full list inside the already-searched square cannot be beaten.
      const reach = ring * NODE_CELL;
      if (n === want && worst <= reach * reach) break;
    }
    return n;
  }
}

export interface NodeFootprintBounds { x0: number; y0: number; x1: number; y1: number }

function footprintBounds(tile: number, kind: number, size = nodeKind(kind).footprint): NodeFootprintBounds {
  const radius = Math.floor(size / 2), x = tile % MAP_W, y = Math.floor(tile / MAP_W);
  return { x0: x - radius, y0: y - radius, x1: x + radius, y1: y + radius };
}

/** Inclusive occupied tile bounds, including the saved legacy footprint. */
export function nodeFootprintBounds(nodes: ResourceNodeStore, i: number): NodeFootprintBounds {
  return footprintBounds(nodes.tile[i], nodes.kind[i], nodes.footprint[i] || 1);
}

/** Complete empty ground for a new tree/outcrop; never cut a road or watercourse. */
export function nodeFootprintClear(map: TileMap, nodes: ResourceNodeStore, cx: number, cy: number, kind: number): boolean {
  const r = Math.floor(nodeKind(kind).footprint / 2);
  for (let y = cy - r; y <= cy + r; y++) for (let x = cx - r; x <= cx + r; x++) {
    if (!map.canBuild(x, y)) return false;
    const tile = map.idx(x, y), terrain = map.terrain[tile];
    if (nodes.at(tile) >= 0 || terrain === Terrain.Road || terrain === Terrain.Marsh || terrain === Terrain.Bridge) return false;
  }
  return true;
}

/** Structural world shape avoids a runtime dependency on world.ts. */
interface HarvestWorld { map: TileMap; nodes: ResourceNodeStore; wallTeam?: number }
export interface HarvestPoint { x: number; y: number }
interface HarvestSource { node: number; point: HarvestPoint }

function harvestWalkable(w: HarvestWorld, x: number, y: number): boolean {
  if (!w.map.inBounds(x, y)) return false;
  const tile = w.map.idx(x, y), wall = w.map.wall[tile];
  if (w.map.building[tile] >= 0) return false;
  if (wall !== WallKind.None && !(wall === WallKind.Gate && alliedWith(Team.Player, w.wallTeam ?? Team.Player))) return false;
  if (w.map.terrain[tile] === Terrain.Deep) return false;
  const node = w.nodes.at(tile);
  return node < 0 || w.nodes.footprint[node] <= 1;
}

function workPoints(w: HarvestWorld, node: number): HarvestPoint[] {
  const b = nodeFootprintBounds(w.nodes, node), out: HarvestPoint[] = [];
  for (let y = b.y0 - 1; y <= b.y1 + 1; y++) for (let x = b.x0 - 1; x <= b.x1 + 1; x++) {
    // Cardinal edge tiles: miners stand alongside a face, never in the mass.
    if ((x < b.x0 || x > b.x1) === (y < b.y0 || y > b.y1)) continue;
    if (harvestWalkable(w, x, y)) out.push({ x: x + 0.5, y: y + 0.5 });
  }
  return out;
}

export function harvestWorkPoint(w: HarvestWorld, node: number, fromX: number, fromY: number): HarvestPoint | null {
  if (!w.nodes.isAlive(node) || w.nodes.amount[node] <= 0) return null;
  const points = workPoints(w, node);
  points.sort((a, b) => (a.x - fromX) ** 2 + (a.y - fromY) ** 2 - (b.x - fromX) ** 2 - (b.y - fromY) ** 2);
  return points.find(point => harvestWalkTarget(w, fromX, fromY, point.x, point.y) !== null) ?? null;
}

/** A reachable tile at the receiving workshop, outside its blocked foundation. */
export function harvestDropPoint(w: HarvestWorld, tx: number, ty: number, bw: number, bh: number, fromX: number, fromY: number): HarvestPoint | null {
  const points: HarvestPoint[] = [];
  for (let y = ty - 1; y <= ty + bh; y++) for (let x = tx - 1; x <= tx + bw; x++) {
    if ((x < tx || x >= tx + bw) === (y < ty || y >= ty + bh)) continue;
    if (harvestWalkable(w, x, y)) points.push({ x: x + 0.5, y: y + 0.5 });
  }
  points.sort((a, b) => (a.x - fromX) ** 2 + (a.y - fromY) ** 2 - (b.x - fromX) ** 2 - (b.y - fromY) ** 2);
  return points.find(point => harvestWalkTarget(w, fromX, fromY, point.x, point.y) !== null) ?? null;
}

const HARVEST_SPAN = HARVEST_RADIUS * 2 + 11;
const floodSeen = new Uint8Array(HARVEST_SPAN * HARVEST_SPAN);
const floodQueue = new Int32Array(floodSeen.length);
const floodParent = new Int32Array(floodSeen.length);
const sourceCandidates = new Int32Array(256);
const CARDINAL = [[0, -1], [-1, 0], [1, 0], [0, 1]] as const;

/** One bounded flood for placement, claims and readout; no saved or stale route state. */
export function harvestSourcesAtPlacement(w: HarvestWorld, kind: number, tx: number, ty: number, bw: number, bh: number): HarvestSource[] {
  const cx = tx + bw / 2, cy = ty + bh / 2;
  const count = w.nodes.nearestNodes(cx, cy, HARVEST_RADIUS, kind, sourceCandidates);
  if (count === 0) return [];
  const x0 = Math.floor(cx) - Math.floor(HARVEST_SPAN / 2), y0 = Math.floor(cy) - Math.floor(HARVEST_SPAN / 2);
  floodSeen.fill(0);
  let head = 0, tail = 0;
  const visit = (x: number, y: number) => {
    const dx = x - x0, dy = y - y0;
    if (dx < 0 || dy < 0 || dx >= HARVEST_SPAN || dy >= HARVEST_SPAN) return;
    const at = dy * HARVEST_SPAN + dx;
    if (floodSeen[at] || (x >= tx && y >= ty && x < tx + bw && y < ty + bh) || !harvestWalkable(w, x, y)) return;
    floodSeen[at] = 1;
    floodQueue[tail++] = at;
  };
  for (let y = ty - 1; y <= ty + bh; y++) for (let x = tx - 1; x <= tx + bw; x++) visit(x, y);
  while (head < tail) {
    const at = floodQueue[head++], x = x0 + at % HARVEST_SPAN, y = y0 + Math.floor(at / HARVEST_SPAN);
    for (const [dx, dy] of CARDINAL) visit(x + dx, y + dy);
  }
  const out: HarvestSource[] = [];
  for (let j = 0; j < count; j++) {
    const node = sourceCandidates[j];
    const points = workPoints(w, node).filter(p => {
      const x = Math.floor(p.x) - x0, y = Math.floor(p.y) - y0;
      return x >= 0 && y >= 0 && x < HARVEST_SPAN && y < HARVEST_SPAN && floodSeen[y * HARVEST_SPAN + x] === 1;
    });
    points.sort((a, b) => (a.x - cx) ** 2 + (a.y - cy) ** 2 - (b.x - cx) ** 2 - (b.y - cy) ** 2);
    if (points[0]) out.push({ node, point: points[0] });
  }
  return out;
}

export function harvestNodeAtPlacement(w: HarvestWorld, kind: number, tx: number, ty: number, bw: number, bh: number): number {
  return harvestSourcesAtPlacement(w, kind, tx, ty, bw, bh)[0]?.node ?? -1;
}

/** Route short harvest legs around walls, foundations and the solid mineral mass. */
export function harvestWalkTarget(w: HarvestWorld, fromX: number, fromY: number, toX: number, toY: number): HarvestPoint | null {
  const dx = toX - fromX, dy = toY - fromY, steps = Math.ceil(Math.hypot(dx, dy) * 4);
  let clear = true;
  for (let s = 1; s <= steps; s++) {
    if (!harvestWalkable(w, Math.floor(fromX + dx * s / steps), Math.floor(fromY + dy * s / steps))) { clear = false; break; }
  }
  if (clear) return { x: toX, y: toY };
  const x0 = Math.floor(fromX) - Math.floor(HARVEST_SPAN / 2), y0 = Math.floor(fromY) - Math.floor(HARVEST_SPAN / 2);
  const goalX = Math.floor(toX) - x0, goalY = Math.floor(toY) - y0;
  if (goalX < 0 || goalY < 0 || goalX >= HARVEST_SPAN || goalY >= HARVEST_SPAN) return null;
  const start = Math.floor(HARVEST_SPAN / 2) * (HARVEST_SPAN + 1), goal = goalY * HARVEST_SPAN + goalX;
  floodSeen.fill(0);
  floodSeen[start] = 1;
  floodQueue[0] = start;
  let head = 0, tail = 1;
  while (head < tail && !floodSeen[goal]) {
    const at = floodQueue[head++], x = at % HARVEST_SPAN, y = Math.floor(at / HARVEST_SPAN);
    for (const [dx, dy] of CARDINAL) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= HARVEST_SPAN || ny >= HARVEST_SPAN) continue;
      const next = ny * HARVEST_SPAN + nx;
      if (floodSeen[next] || !harvestWalkable(w, x0 + nx, y0 + ny)) continue;
      floodSeen[next] = 1;
      floodParent[next] = at;
      floodQueue[tail++] = next;
    }
  }
  if (!floodSeen[goal]) return null;
  let next = goal;
  while (floodParent[next] !== start && next !== start) next = floodParent[next];
  return { x: x0 + next % HARVEST_SPAN + 0.5, y: y0 + Math.floor(next / HARVEST_SPAN) + 0.5 };
}

function distTo(s: ResourceNodeStore, i: number, x: number, y: number): number {
  const t = s.tile[i];
  const dx = (t % MAP_W) + 0.5 - x;
  const dy = ((t / MAP_W) | 0) + 0.5 - y;
  return dx * dx + dy * dy;
}

/**
 * Cell size for the node grid.
 *
 * Four tiles rather than the spatial hash's two: the dominant query is "the
 * nearest tree within `HARVEST_RADIUS`", which is a radius of nine, and a node
 * never moves — so the grid is sized to the query rather than to the churn.
 */
const NODE_CELL = 4;
const CELLS_X = Math.ceil(MAP_W / NODE_CELL);
const CELLS_Y = Math.ceil(MAP_H / NODE_CELL);
const CELL_COUNT = CELLS_X * CELLS_Y;
/** How many felled nodes the grid tolerates before it is worth rebuilding. */
const GRID_REBUILD_DEAD = 1024;

function cellIndex(x: number, y: number): number {
  return clampCell(Math.floor(y / NODE_CELL), CELLS_Y) * CELLS_X + clampCell(Math.floor(x / NODE_CELL), CELLS_X);
}

function clampCell(v: number, span: number): number {
  return v < 0 ? 0 : v >= span ? span - 1 : v;
}

/**
 * Stand a node on every lattice block of matching terrain.
 *
 * **No RNG at all**, and that is a decision rather than an omission. Seeding is
 * a pure walk of the terrain the generator has already painted, so:
 *
 *   the trees stand where the wood is, which is the brief's "a map still looks
 *   like the map it was";
 *   nothing here consumes a draw, so adding nodes cannot shift the stream that
 *   decides gates, rivers or anything else the map pass rolls for — a map
 *   generated by this build is tile-for-tile the map the last build generated;
 *   and a save written *before* nodes existed can be seeded on load from its own
 *   terrain and get exactly the trees it would have had, with no stored seed to
 *   recover. That last one is the whole migration.
 *
 * A block contributes at most one node, at the first matching tile in row-major
 * order that is clear of walls, buildings and rubble.
 */
export function seedResourceNodes(map: TileMap, nodes: ResourceNodeStore): void {
  nodes.reset();
  for (const def of NODE_KINDS) {
    const L = def.lattice;
    const cells = L * L;
    for (let by = 0; by < MAP_H; by += L) {
      for (let bx = 0; bx < MAP_W; bx += L) {
        /**
         * Where in the block to start looking — hashed, not the corner.
         *
         * This used to scan `dy` then `dx` from zero and take the first match,
         * which in solid forest is *always* `(bx, by)`. Every node therefore
         * landed on the lattice itself, and a wood came out as a parade ground:
         * a perfectly regular grid of trees, plainly visible the moment the
         * renderer drew them (shot `25-the-wood`). Nothing was wrong with the
         * simulation and everything was wrong with the picture.
         *
         * Walking the block from a hashed offset and wrapping keeps every
         * property the lattice was chosen for. It is still **a pure walk of the
         * terrain with no rolls in it** — `hash2` consumes no `Rng` stream, so
         * seeding still cannot shift which wave arrives or what a relic drops —
         * and it still places exactly one node per block wherever the block
         * holds any eligible tile at all. Two worlds with the same map still
         * grow the same trees in the same order. They are simply no longer in
         * rows.
         */
        const start = (cells * hash2(bx, by, SALT_SEED_SCATTER)) | 0;
        for (let s = 0; s < cells; s++) {
          const o = (start + s) % cells;
          const x = bx + (o % L);
          const y = by + ((o / L) | 0);
          if (x >= MAP_W || y >= MAP_H) continue;
          const k = y * MAP_W + x;
          if (map.terrain[k] !== def.terrain) continue;
          if (!nodeFootprintClear(map, nodes, x, y, def.kind)) continue;
          if (nodes.spawn(k, def.kind, def.amount) < 0) return; // store full
          break;
        }
      }
    }
  }
}

/**
 * Take up to `want` off a node. Returns what was actually taken.
 *
 * The charge is in **raw** units — never scaled by season, difficulty, district
 * or the modifier bag. A tree holds what a tree holds; what a settlement gets
 * for it is decided at the storehouse door, in `stepProduction`'s multiplier
 * stack, and applied to the load when it is put down. Keeping the two apart is
 * what makes a node's life a property of the map rather than of who is standing
 * on it, which is the only version of this a save can round-trip honestly.
 */
export function takeFromNode(nodes: ResourceNodeStore, i: number, want: number): number {
  if (!nodes.isAlive(i)) return 0;
  const got = Math.min(want, nodes.amount[i]);
  nodes.amount[i] -= got;
  if (nodes.amount[i] < 1e-4) nodes.amount[i] = 0;
  return got;
}

/**
 * Fell it: take the node off the world and let the ground go back to ash.
 *
 * Returns true if the terrain actually changed, so the caller can bump
 * `map.terrainVersion` (the renderer re-bakes off it) and mark the flow weights
 * dirty (ash plain is faster to cross than deadwood). Both of those want a
 * `World` and this file may not have one — see the note at the top.
 *
 * The ground reverts **only if it is still the terrain the node was seeded
 * from**. A tile that has since been paved, scorched or dug is somebody else's
 * decision and a felled tree is not entitled to undo it.
 */
export function removeNode(map: TileMap, nodes: ResourceNodeStore, i: number): boolean {
  if (!nodes.isAlive(i)) return false;
  const def = nodeKind(nodes.kind[i]);
  const bounds = nodeFootprintBounds(nodes, i);
  let changed = false;
  nodes.kill(i);
  for (let y = Math.max(0, bounds.y0); y <= Math.min(MAP_H - 1, bounds.y1); y++) {
    for (let x = Math.max(0, bounds.x0); x <= Math.min(MAP_W - 1, bounds.x1); x++) {
      const k = y * MAP_W + x;
      if (map.terrain[k] === def.terrain && nodes.at(k) < 0) {
        map.terrain[k] = def.revertTo;
        changed = true;
      }
    }
  }
  if (changed) map.terrainVersion++;
  return changed;
}

/**
 * Clear every node standing under a rectangle of tiles, without reverting the
 * ground.
 *
 * Called when a building is stamped: a camp is built in the clearing it makes,
 * and the trees that were standing there are gone. The ground is deliberately
 * *not* reverted — the footprint of a woodcutter's camp is still forest, which
 * is what `needsTerrain` reads and what keeps a second camp legal beside the
 * first. Felling the wood is what reverts ground; putting a shed on it is not.
 */
export function clearNodesInRect(nodes: ResourceNodeStore, x0: number, y0: number, x1: number, y1: number): number {
  let n = 0;
  for (let y = Math.max(0, y0); y <= Math.min(MAP_H - 1, y1); y++) {
    for (let x = Math.max(0, x0); x <= Math.min(MAP_W - 1, x1); x++) {
      let i = nodes.at(y * MAP_W + x);
      while (i >= 0) {
        nodes.kill(i);
        n++;
        i = nodes.at(y * MAP_W + x);
      }
    }
  }
  return n;
}

/**
 * How worn down a node looks, in the art layer's four words.
 *
 * The vocabulary is `artgen`'s (`ARTMANIFEST.md`), and it is answered here
 * rather than in the renderer so there is one definition of "nearly finished"
 * and a picture cannot disagree with the simulation about it.
 *
 * **Only three of the four can ever come back from a live node**, and the fourth
 * is worth stating plainly rather than leaving a renderer to discover: a node
 * that reaches zero is *removed* and the ground under it reverts, so there is no
 * `gone` node to draw. The scar is a property of the **ground**, not of an
 * object standing on it — see `removeNode`, and `TileMap.stain`, which is
 * already the layer for "something happened here" and is already saved.
 */
export const NodeStage = {
  Full: 'full',
  Worked: 'worked',
  Spent: 'spent',
  Gone: 'gone',
} as const;
export type NodeStageId = (typeof NodeStage)[keyof typeof NodeStage];

export function nodeStage(fraction: number): NodeStageId {
  if (fraction >= 0.7) return NodeStage.Full;
  if (fraction >= 0.3) return NodeStage.Worked;
  if (fraction > 0) return NodeStage.Spent;
  return NodeStage.Gone;
}

/** What a UI needs to draw a node without re-deriving any of it. */
export interface NodeReadout {
  index: number;
  kind: number;
  name: string;
  x: number;
  y: number;
  amount: number;
  max: number;
  /** 0..1, for a bar over the stump. */
  fraction: number;
  /** `full` / `worked` / `spent`. See `nodeStage`. */
  stage: NodeStageId;
}

export function nodeReadout(nodes: ResourceNodeStore, i: number): NodeReadout | null {
  if (!nodes.isAlive(i)) return null;
  const t = nodes.tile[i];
  const def = nodeKind(nodes.kind[i]);
  const fraction = nodes.max[i] > 0 ? nodes.amount[i] / nodes.max[i] : 0;
  return {
    index: i,
    kind: nodes.kind[i],
    name: def.name,
    x: (t % MAP_W) + 0.5,
    y: ((t / MAP_W) | 0) + 0.5,
    amount: nodes.amount[i],
    max: nodes.max[i],
    fraction,
    stage: nodeStage(fraction),
  };
}

/** Everything of one kind inside a box, for the renderer's cull. */
export function nodesInBox(
  nodes: ResourceNodeStore,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  out: number[],
): void {
  out.length = 0;
  for (let i = 0; i < nodes.slots.high; i++) {
    if (nodes.slots.alive[i] === 0) continue;
    const t = nodes.tile[i];
    const x = t % MAP_W;
    const y = (t / MAP_W) | 0;
    if (x < x0 || x > x1 || y < y0 || y > y1) continue;
    out.push(i);
  }
}

/**
 * Whether this terrain is one a node is ever seeded from.
 *
 * Read off `NODE_KINDS` rather than a second list of three terrains, for the
 * reason `terrainName` in `apply.ts` reads off `TERRAIN_NAMES`: a hand-kept
 * parallel list falls a terrain behind and nothing fails when it does.
 */
export function isNodeTerrain(t: number): boolean {
  return nodeKindForTerrain(t) >= 0;
}
